import {
  Typography,
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  CardHeader,
  Card,
  CardContent,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { Control, Controller, FieldErrors, UseFormSetValue } from "react-hook-form";
import type { SubscriptionFormData } from "@/pages/subscriptions/services/types";
import type { MetricDefinition } from "@/pages/subscriptions/services/types";
import type { GroupedBySource } from "../../types";

interface EntityDetailsCardProps {
  control: Control<SubscriptionFormData>;
  errors: FieldErrors<SubscriptionFormData>;
  setValue: UseFormSetValue<SubscriptionFormData>;
  source: string;
  groupedBySource: GroupedBySource;
  categoryDisplayNameLookup: Record<string, string>;
  categoryToSourceLookup: Record<string, string>;
  metricsForCategory: MetricDefinition[];
}

const EntityDetailsCard = ({
  control,
  errors,
  setValue,
  source,
  groupedBySource,
  categoryDisplayNameLookup,
  categoryToSourceLookup,
  metricsForCategory,
}: EntityDetailsCardProps) => {
  // Group categories by source so the dropdown carries platform identity
  // (e.g. "Snowflake → Account", "Databricks → Account"). Without this the
  // user can't tell apart overlapping entities across platforms.
  const sourceGroups = Object.entries(groupedBySource).map(([sourceKey, sourceData]) => ({
    sourceKey,
    sourceLabel: sourceData.source_display_label ?? sourceKey,
    categories: Object.keys(sourceData.categories),
  }));
  const showSourceLabels = sourceGroups.length > 1;

  return (
    <Card>
      <CardHeader>
        <Typography variant="body">Entity Details</Typography>
      </CardHeader>
      <CardContent>
        <Stack gap="md" justify="around">
          <div className="al-flex-1">
            <Label htmlFor="source">
              Select a category <span className="al-text-red-600">*</span>
            </Label>
            <Controller
              name="source"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={category => {
                    field.onChange(category);
                    // Also set the source_name when category changes
                    const sourceName = categoryToSourceLookup[category];
                    if (sourceName) {
                      setValue("source_name", sourceName);
                      setValue("platform", sourceName);
                    }
                  }}
                >
                  <SelectTrigger id="source" className="al-mt-2 al-w-full">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceGroups.map(group => (
                      <SelectGroup key={group.sourceKey}>
                        {showSourceLabels && <SelectLabel>{group.sourceLabel}</SelectLabel>}
                        {group.categories.map(category => (
                          <SelectItem key={`${group.sourceKey}:${category}`} value={category}>
                            {categoryDisplayNameLookup[category] ?? category}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.source && (
              <Typography variant="body" size="xs" className="al-text-red-500 al-mt-1 al-block">
                {errors.source.message}
              </Typography>
            )}
          </div>
          <div className="al-flex-1">
            <Label htmlFor="metric">
              Select metric <span className="al-text-red-600">*</span>
            </Label>
            <Controller
              name="metric"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={!source || metricsForCategory.length === 0}
                >
                  <SelectTrigger id="metric" className="al-mt-2 al-w-full">
                    <SelectValue
                      placeholder={!source ? "Select a category first" : "Select a metric"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {metricsForCategory.map(m => (
                      <SelectItem key={m.name} value={m.name}>
                        {m.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.metric && (
              <Typography variant="body" size="xs" className="al-text-red-500 al-mt-1 al-block">
                {errors.metric.message}
              </Typography>
            )}
          </div>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default EntityDetailsCard;
